import React from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import dayjs from 'dayjs'
import useTransactionDetail from '../../hooks/queries/useTransactionDetail'
import MainContentDiv from '../../components/templates/MainContent'
import SearchContainer from '../../components/organisms/SearchContainer'
import Search from '../Search'
import ContentsTitle from '../../components/molecules/ContentsTitle'
import Tab from '../../components/molecules/Tab'
import ContentsWrapper from '../../components/organisms/ContentsWrapper'
import Description from '../../components/molecules/Description'
import Copy from '../../components/atoms/Copy'
import { TransactionStatusCode } from '../../api/transaction/response'
import Button from '../../components/atoms/Button'
import { weiToEther } from '../../utils/dataFormat'
import PageLoading from '../../components/organisms/PageLoading'
import { EllipsisText, StatusColor, TokenTicker, TokenTransferredValue } from './styles'

function TransactionDetails() {
  const navigate = useNavigate()
  const { txHash } = useParams() as { txHash: string }
  const {
    transactionDetail: txDetail = {
      status: null,
      txHash: '',
      method: '',
      blockTimestamp: 0,
      blockNumber: 0,
      from: {
        address: '',
        nameTag: '',
        isContract: false,
      },
      to: {
        address: '',
        nameTag: '',
        isContract: false,
      },
      tokenTransferList: [],
      value: '',
      txFee: '',
      symbol: '',
    },
    isLoading,
  } = useTransactionDetail(txHash)
  const onClickBlockNumber = () => navigate(`/block/${txDetail.blockNumber}`)
  const onClickAddress = (address: string) => () => navigate(`/account/${address}`)

  return (
    <MainContentDiv>
      <SearchContainer>
        <Search />
      </SearchContainer>
      <ContentsTitle title="Transactions Details" />
      <Tab items={[{ text: 'Overview' }]} selected="Overview" />
      <ContentsWrapper>
        <Description
          dataSource={[
            {
              title: 'Txn hash',
              desc: (
                <>
                  {txDetail.txHash}
                  <Copy text={txDetail.txHash} />
                </>
              ),
            },
            {
              title: 'Status',
              desc: (
                <>
                  {/* eslint-disable-next-line no-nested-ternary */}
                  {txDetail.status === TransactionStatusCode.SUCCESS ? (
                    <StatusColor status="success">Success</StatusColor>
                  ) : txDetail.status === TransactionStatusCode.FAILED ? (
                    <StatusColor status="fail">Failed</StatusColor>
                  ) : (
                    <StatusColor status="pending">Pending</StatusColor>
                  )}
                </>
              ),
            },
            {
              title: 'Block',
              desc: <Button buttonTheme="defaultBlue" text={txDetail.blockNumber} onClick={onClickBlockNumber} />,
            },
            {
              title: 'Timestamp',
              desc: (
                <>
                  {dayjs.unix(Number(txDetail.blockTimestamp)).fromNow()}
                  &nbsp; ({dayjs.unix(Number(txDetail.blockTimestamp)).utc().format('MMM-DD-YYYY hh:mm:ss a')} +UTC)
                </>
              ),
            },
            {
              title: 'From',
              desc: (
                <>
                  <Button
                    buttonTheme="defaultBlue"
                    text={txDetail.from.address}
                    onClick={onClickAddress(txDetail.from.address)}
                  />
                  <Copy text={txDetail.from.address} />
                </>
              ),
            },
            {
              title: 'To',
              desc: txDetail.to ? (
                <>
                  <Button
                    buttonTheme="defaultBlue"
                    text={txDetail.to.address}
                    onClick={onClickAddress(txDetail.to.address)}
                  />
                  <Copy text={txDetail.to.address} />
                </>
              ) : (
                ''
              ),
            },
            {
              title: 'Token Transferred',
              desc: txDetail.tokenTransferList.map(({ from, to, tokenImage, symbol, value }) => (
                <TokenTransferredValue>
                  From&nbsp;
                  <EllipsisText isAddress onClick={onClickAddress(from.address)}>
                    {from.address}
                  </EllipsisText>
                  To&nbsp;
                  <EllipsisText isAddress onClick={onClickAddress(to.address)}>
                    {to.address}
                  </EllipsisText>
                  For&nbsp;
                  <EllipsisText>{weiToEther(value)}</EllipsisText>
                  <TokenTicker src={tokenImage} />
                  &nbsp;{symbol}
                </TokenTransferredValue>
              )),
              isHide: !txDetail.tokenTransferList.length,
            },
            {
              title: 'Value',
              desc: (
                <>
                  {weiToEther(txDetail.value)} {txDetail.symbol}
                </>
              ),
            },
            {
              title: 'Txn Fee',
              desc: (
                <>
                  {weiToEther(txDetail.txFee)} {txDetail.symbol}
                </>
              ),
            },
          ]}
        />
      </ContentsWrapper>
      <PageLoading loading={isLoading} />
    </MainContentDiv>
  )
}

export default TransactionDetails
